import type { ReactNode } from 'react';
import { Truck } from 'lucide-react';
import { APP_CONFIG } from '../../core/config';
import { canSeeRoleBadge, ROLE_LABELS } from '../../core/permissions';
import { preferencesOf } from '../../db/auth-store';
import { navItem } from './nav-items';
import type { NavItemId } from './nav-items';
import type { User } from '../../types/user';

interface TopBarProps {
  active: NavItemId;
  user: User;
  /** The cloud-mirror indicator, when the shell is running one. */
  status?: ReactNode;
}

/**
 * Sticky application header — app identity on one side, the operator on the
 * other.
 *
 * The role badge underneath the name is the super-admin's own indicator: it is
 * shown to that account, about itself, and to nobody else. A soldier's header
 * carries their name alone — roles are read on the management screens, which
 * name them explicitly.
 */
export function TopBar({ active, user, status }: TopBarProps) {
  const current = navItem(active);
  const preferences = preferencesOf(user);
  const showsRoleBadge = canSeeRoleBadge(user) && preferences.showRoleBadge;

  return (
    <header
      data-print="hide"
      className="sticky top-0 z-30 print:hidden bg-panel/95 backdrop-blur border-b border-line"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="lg:hidden w-9 h-9 shrink-0 rounded-xl bg-brand-50 text-brand-500 flex items-center justify-center">
            <Truck size={18} aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="font-bold leading-tight truncate">{current.title}</h1>
            <p className="text-xs text-ink/50 truncate lg:hidden">{APP_CONFIG.appName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {status}
          <div className="text-left">
            <p className="text-sm font-medium truncate max-w-[10rem]">{user.fullName}</p>
            {showsRoleBadge ? (
              <p className="text-xs text-ink/50">{ROLE_LABELS[user.role]}</p>
            ) : null}
            {preferences.showUnitLine ? (
              <p className="text-xs text-ink/40 truncate max-w-[10rem]">
                {user.company} · {user.platoon}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
